'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
interface ScrollPosition {
  x: number;
  y: number;
}
interface UseScrollPositionOptions {
  /**
   * Throttle delay in milliseconds
   * @default 0
   */
  throttleMs?: number;
}

/**
 * A reusable hook to track the current scroll position
 * @param options - Configuration options
 * @returns The current scroll position { x, y }
 */
export function useScrollPosition(t0) {
  const $ = _c(6);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    throttleMs: t2
  } = options;
  const throttleMs = t2 === undefined ? 0 : t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      x: 0,
      y: 0
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [scrollPosition, setScrollPosition] = useState(t3);
  let t4;
  let t5;
  if ($[3] !== throttleMs) {
    t4 = () => {
      let timeoutId = null;
      let lastRun = 0;
      const handleScroll = () => {
        const now = Date.now();
        if (throttleMs > 0) {
          if (now - lastRun >= throttleMs) {
            lastRun = now;
            setScrollPosition({
              x: window.scrollX,
              y: window.scrollY
            });
          } else {
            if (!timeoutId) {
              timeoutId = setTimeout(() => {
                lastRun = Date.now();
                setScrollPosition({
                  x: window.scrollX,
                  y: window.scrollY
                });
                timeoutId = null;
              }, throttleMs - (now - lastRun));
            }
          }
        } else {
          setScrollPosition({
            x: window.scrollX,
            y: window.scrollY
          });
        }
      };
      handleScroll();
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    };
    t5 = [throttleMs];
    $[3] = throttleMs;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  return scrollPosition;
}

/**
 * A convenience hook that returns whether the page is scrolled from top
 * @param threshold - The scroll threshold in pixels (default: 0)
 * @returns true if scrolled past threshold, false otherwise
 */
export function useIsScrolled(t0) {
  const threshold = t0 === undefined ? 0 : t0;
  const {
    y
  } = useScrollPosition();
  return y > threshold;
}